"use client";

import Icon from "@ant-design/icons";
import { FunctionComponent, useMemo, useState } from "react";
import CustomButton from "rivals/components/shared/CustomButton";
import ShareIcon from "rivals/components/shared/Icons/ShareIcon";
import { gaSagaEvent } from "rivals/shared/analytics";
import {
  PATHS,
  ROOT_SITE_IDENTIFIER,
  SOCIAL_MEDIA
} from "rivals/shared/constants";
import { AthleteViews, CollegePlayer } from "rivals/shared/interfaces/Athlete";
import { buildSiteUrl } from "rivals/shared/utils/routes";
import EmbedProspectModal from "./EmbedProspectModal";
import styles from "./ShareMenu.module.scss";
import strings from "./strings";
import { Props, ShareMenuVariant } from "./types";
import SocialMediaShareIcon from "../SocialMediaShareIcon";

const {
  emailIcon,
  emailShare,
  embedIcon,
  newWindow,
  printIcon,
  printTitle,
  rivals
} = strings;

const getPlayerType = (
  activeAthleteView: AthleteViews,
  collegePlayer?: CollegePlayer
): string => {
  return activeAthleteView === AthleteViews.COLLEGE_PLAYER_VIEW && collegePlayer
    ? "college player"
    : "prospect";
};

const ShareMenu: FunctionComponent<Props> = ({
  activeAthleteView = AthleteViews.PROSPECT_VIEW,
  collegePlayer,
  gallery,
  prospect,
  variant = ShareMenuVariant.ATHLETE
}) => {
  const [showShareMenu, toggleShareMenu] = useState(false);
  const [showEmbedModal, toggleShowEmbedModal] = useState(false);

  const { openMenuStyle, pageUrl, socialMediaText, emailUrl } = useMemo(() => {
    const isProspect = prospect && activeAthleteView;
    const galleryUrl = encodeURI(
      buildSiteUrl(gallery?.siteIdentifier ?? ROOT_SITE_IDENTIFIER) +
        PATHS.GALLERIES.replace("[id]", `${gallery?.id}`)
    );
    switch (variant) {
      case ShareMenuVariant.ATHLETE:
        return {
          emailUrl: emailShare
            .replace("[emailSubject]", encodeURIComponent(printTitle))
            .replace("[emailBody]", encodeURIComponent("content.emailUrl")),
          openMenuStyle: isProspect ? "Prospect" : "Content",
          pageUrl: isProspect
            ? encodeURI(`${prospect.athleteUrl}?view=${activeAthleteView}`)
            : encodeURI("content.pageUrl"),
          socialMediaText: isProspect
            ? `${encodeURI(prospect.fullName + rivals)}`
            : "Rivals.com"
        };
      case ShareMenuVariant.GALLERY:
        return {
          emailUrl: emailShare
            .replace(
              "[emailSubject]",
              `Check out this gallery from ${gallery?.originalSite}`
            )
            .replace(
              "[emailBody]",
              `${gallery?.name} \n ${gallery?.summary} \n ${galleryUrl}`
            ),
          openMenuStyle: "Content",
          pageUrl: galleryUrl,
          socialMediaText: "Rivals.com"
        };
    }
  }, [activeAthleteView, gallery, prospect, variant]);

  const onClickAnySocialMediaIcon = (media: string): void => {
    if (
      (prospect || collegePlayer) &&
      activeAthleteView &&
      showShareMenu &&
      variant === ShareMenuVariant.ATHLETE
    ) {
      const playerType = getPlayerType(activeAthleteView, collegePlayer);
      const playerId =
        activeAthleteView === AthleteViews.COLLEGE_PLAYER_VIEW && collegePlayer
          ? collegePlayer.collegePlayerId
          : prospect?.prospectId;
      gaSagaEvent({
        eventName: "share",
        eventParams: {
          contentType: `${playerType} profile`,
          itemId: playerId?.toString(),
          method: media
        }
      });
    }

    if (variant === ShareMenuVariant.GALLERY && showShareMenu && gallery) {
      gaSagaEvent({
        eventName: "share",
        eventParams: {
          contentType: "gallery",
          itemId: gallery.id,
          method: media
        }
      });
    }
  };

  const additionalShareOptions = prospect ? (
    <CustomButton
      ariaLabel="embed code"
      className={styles.icon}
      icon={
        <img
          alt=""
          className={styles.iconImage}
          src={embedIcon}
          title="Embed"
        />
      }
      onClick={(): void => {
        toggleShowEmbedModal(true);
        onClickAnySocialMediaIcon("embed");
      }}
      philter="embed"
    />
  ) : (
    <>
      <CustomButton
        ariaLabel="share via mail"
        className={styles.icon}
        icon={
          <img
            alt=""
            className={styles.iconImage}
            src={emailIcon}
            title="Email"
          />
        }
        onClick={(): void => {
          window.open(emailUrl, newWindow);
          onClickAnySocialMediaIcon("email");
        }}
        philter="email"
      />

      <CustomButton
        ariaLabel="print"
        className={styles.icon}
        icon={
          <img
            alt=""
            className={styles.iconImage}
            src={printIcon}
            title="Print"
          />
        }
        onClick={(): void => {
          window.open("printUrl", newWindow);
          onClickAnySocialMediaIcon("print");
        }}
        philter="print"
      />
    </>
  );

  const shareMenu = (
    <div
      className={
        showShareMenu
          ? styles[`shareIconMenu${openMenuStyle}Open`]
          : styles.shareIconMenuClosed
      }
    >
      {showShareMenu && (
        <div className={styles.iconHolder}>
          {Object.values(SOCIAL_MEDIA).map(socialMedia => (
            <SocialMediaShareIcon
              className={styles.icon}
              key={socialMedia}
              onClickAnySocialMediaIcon={onClickAnySocialMediaIcon}
              pageUrl={pageUrl}
              socialMedia={socialMedia}
              text={socialMediaText}
              theme="dark"
            />
          ))}
          {additionalShareOptions}
        </div>
      )}
    </div>
  );

  const prospectShare = (
    <>
      {shareMenu}
      <CustomButton
        ariaLabel={`${showShareMenu ? "hide" : "open"} share menu`}
        className={showShareMenu ? styles.openShareIcon : styles.shareIcon}
        icon={
          <Icon
            className={styles.shareSvg}
            component={ShareIcon}
            rev={""}
            translate="yes"
          />
        }
        onClick={(): void => toggleShareMenu(!showShareMenu)}
        philter="share"
      />
      {showEmbedModal && prospect && (
        <EmbedProspectModal
          activeAthleteView={activeAthleteView}
          closeModal={(): void => {
            toggleShowEmbedModal(false);
          }}
          prospect={prospect}
          showModal={showEmbedModal}
        />
      )}
    </>
  );
  return <div className={styles.shareMenu}>{prospectShare}</div>;
};

export default ShareMenu;
